/**
 * LIVE PULLS 시뮬레이션 피드 — 웹게임 라이브니스 신호(데모 핵심).
 * oripa RECENT_PULLS를 시드로 전역 피드 싱글턴을 만들고, 구독자가 있는 동안
 * 7~16초 간격으로 새 개봉이 도착한다(팩 인기 가중치 × 팩 내 그룹 확률 롤).
 * 상대시간은 20초마다 갱신 — "2m ago"가 얼어붙지 않는다.
 * 홈 S4 LIVE PULLS(전역)와 팩 상세 Recent pulls(팩 필터)가 같은 스트림 공유.
 * TODO(server): 실시간 개봉 피드 RPC로 교체 — 이 파일 전체가 대체 대상.
 */
import { useMemo, useSyncExternalStore } from "react";
import type { CardFinish } from "@/lib/api";
import { HOME_AGENTS } from "@/lib/homeAgents";
import { ORIPA_PACKS, RECENT_PULLS, type OripaPack, type RecentPull } from "@/lib/renewal/oripa";

type LivePull = Omit<RecentPull, "pulledAgo"> & { pulledAt: number };

// ------------------------------------------------------------------- Seeds

/** RECENT_PULLS 순서와 1:1 — mock 문자열("2m ago"…)의 타임스탬프 환산(분) */
const SEED_OFFSETS_MIN = [2, 9, 14, 21, 36, 48, 60, 180, 240, 300, 360];

/** 팩 상세 필터 커버리지 보강 — 시드가 얇은 팩(ember/comet/quasar)에 과거 개봉 추가 */
const EXTRA_SEEDS: (Omit<LivePull, "pulledAt"> & { agoMin: number })[] = [
  { id: "rp-x1", packId: "ember", agentId: "deepseek-chat", finish: "holo", serial: null, agoMin: 75 },
  { id: "rp-x2", packId: "comet", agentId: "openai-gpt", finish: "gold_ice_sapphire", serial: 22, agoMin: 420 },
  { id: "rp-x3", packId: "quasar", agentId: "claude-opus", finish: "red_ice_sapphire", serial: 11, agoMin: 480 },
  { id: "rp-x4", packId: "ember", agentId: "qwen-3-6-plus", finish: "base", serial: null, agoMin: 540 },
];

/** 도착 빈도 가중치 — 싼 팩일수록 자주 열린다(courtyard 관찰 기반 mock) */
const PACK_WEIGHT: Record<string, number> = {
  ember: 30,
  starter: 24,
  comet: 18,
  collector: 12,
  premium: 8,
  quasar: 5,
  baller: 3,
};

const ARRIVAL_MIN_MS = 7_000;
const ARRIVAL_MAX_MS = 16_000;
const AGO_TICK_MS = 20_000;
const FEED_CAP = 28;

// ------------------------------------------------------------------- Store

let feed: LivePull[] | null = null;
let version = 0;
let seq = 0;
const listeners = new Set<() => void>();
let arrivalTimer: ReturnType<typeof setTimeout> | null = null;
let agoTimer: ReturnType<typeof setInterval> | null = null;

function ensureFeed(): LivePull[] {
  if (feed) return feed;
  const now = Date.now();
  const seeded: LivePull[] = [
    ...RECENT_PULLS.map(({ pulledAgo: _drop, ...pull }, i) => ({
      ...pull,
      pulledAt: now - (SEED_OFFSETS_MIN[i] ?? 360 + i * 60) * 60_000,
    })),
    ...EXTRA_SEEDS.map(({ agoMin, ...pull }) => ({ ...pull, pulledAt: now - agoMin * 60_000 })),
  ];
  feed = seeded.sort((a, b) => b.pulledAt - a.pulledAt);
  return feed;
}

function emit(): void {
  version += 1;
  for (const listener of listeners) listener();
}

// --------------------------------------------------------------- Generator

function pickWeightedPack(): OripaPack {
  const total = ORIPA_PACKS.reduce((sum, pack) => sum + (PACK_WEIGHT[pack.id] ?? 10), 0);
  let roll = Math.random() * total;
  for (const pack of ORIPA_PACKS) {
    roll -= PACK_WEIGHT[pack.id] ?? 10;
    if (roll <= 0) return pack;
  }
  return ORIPA_PACKS[0];
}

function serialFor(finish: CardFinish, group: string): number | null {
  if (finish === "base" || finish === "holo") return null;
  const cap = group === "super_grail" ? 25 : group === "grail" ? 60 : 99;
  return 1 + Math.floor(Math.random() * cap);
}

function generatePull(now: number): LivePull {
  const pack = pickWeightedPack();
  let roll = Math.random() * 100;
  let config = pack.groups[0];
  for (const group of pack.groups) {
    roll -= group.oddsPct;
    config = group;
    if (roll <= 0) break;
  }
  const finish = config.finishes[Math.floor(Math.random() * config.finishes.length)];
  const agent = HOME_AGENTS[Math.floor(Math.random() * HOME_AGENTS.length)];
  seq += 1;
  return {
    id: `lp-${seq}`,
    packId: pack.id,
    agentId: agent.agentId,
    finish,
    serial: serialFor(finish, config.group),
    pulledAt: now,
  };
}

function scheduleArrival(): void {
  const delay = ARRIVAL_MIN_MS + Math.random() * (ARRIVAL_MAX_MS - ARRIVAL_MIN_MS);
  arrivalTimer = setTimeout(() => {
    const list = ensureFeed();
    feed = [generatePull(Date.now()), ...list].slice(0, FEED_CAP);
    emit();
    scheduleArrival();
  }, delay);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) {
    scheduleArrival();
    agoTimer = setInterval(emit, AGO_TICK_MS);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      if (arrivalTimer) clearTimeout(arrivalTimer);
      if (agoTimer) clearInterval(agoTimer);
      arrivalTimer = null;
      agoTimer = null;
    }
  };
}

function getVersion(): number {
  return version;
}

// ------------------------------------------------------------------- Hooks

export function relativeAgo(pulledAt: number, now: number = Date.now()): string {
  const sec = Math.max(0, Math.round((now - pulledAt) / 1000));
  if (sec < 45) return "just now";
  const min = Math.max(1, Math.floor(sec / 60));
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

/**
 * 라이브 개봉 피드 — packId 생략 = 전역(TOP PULLS). 반환값은 RecentPull 계약
 * 그대로(pulledAgo 실시간 계산)라 기존 pullTile 매핑을 그대로 쓴다.
 */
export function useLivePulls(packId?: string, limit: number = 12): RecentPull[] {
  const v = useSyncExternalStore(subscribe, getVersion, getVersion);
  return useMemo(() => {
    void v;
    const now = Date.now();
    const list = ensureFeed();
    const filtered = packId ? list.filter((pull) => pull.packId === packId) : list;
    return filtered.slice(0, limit).map(({ pulledAt, ...pull }) => ({
      ...pull,
      pulledAgo: relativeAgo(pulledAt, now),
    }));
  }, [v, packId, limit]);
}
